import { useEffect } from 'react';

// Performance monitoring and optimization utilities
export const usePerformanceMonitoring = () => {
  useEffect(() => {
    // Monitor Core Web Vitals
    if ('PerformanceObserver' in window) {
      try {
        // Monitor Largest Contentful Paint (LCP)
        const lcpObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            console.log('LCP:', entry.startTime);
            // You can send this to analytics
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // Monitor First Input Delay (FID)
        const fidObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const fidEntry = entry as any; // PerformanceEventTiming not in standard types
            console.log('FID:', fidEntry.processingStart - fidEntry.startTime);
            // You can send this to analytics
          }
        });
        fidObserver.observe({ entryTypes: ['first-input'] });

        // Monitor Cumulative Layout Shift (CLS)
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const clsEntry = entry as any; // LayoutShift not in standard types
            if (!clsEntry.hadRecentInput) {
              console.log('CLS:', clsEntry.value);
              // You can send this to analytics
            }
          }
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });

        // Cleanup observers
        return () => {
          lcpObserver.disconnect();
          fidObserver.disconnect();
          clsObserver.disconnect();
        };
      } catch (error) {
        console.warn('Performance monitoring not supported:', error);
      }
    }
  }, []);
};

// Resource loading optimization
export const optimizeResourceLoading = () => {
  // Preload critical CSS
  const preloadCSS = (href: string) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'style';
    link.href = href;
    document.head.appendChild(link);
    
    // Apply CSS after preload
    setTimeout(() => {
      link.rel = 'stylesheet';
    }, 100);
  };

  // Prefetch likely next page resources
  const prefetchResource = (href: string) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    document.head.appendChild(link);
  };

  return { preloadCSS, prefetchResource };
};

// Bundle size tracking
export const trackBundleSize = () => {
  if ('performance' in window && 'navigation' in performance) {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const resourceEntries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    
    // Calculate total JavaScript bundle size
    const jsSize = resourceEntries
      .filter(entry => entry.name.includes('.js'))
      .reduce((total, entry) => total + (entry.transferSize || 0), 0);
    
    // Calculate total CSS size
    const cssSize = resourceEntries
      .filter(entry => entry.name.includes('.css'))
      .reduce((total, entry) => total + (entry.transferSize || 0), 0);
    
    console.log('Bundle Performance:', {
      totalJSSize: `${(jsSize / 1024).toFixed(2)} KB`,
      totalCSSSize: `${(cssSize / 1024).toFixed(2)} KB`,
      domContentLoaded: `${navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart} ms`,
      loadComplete: `${navEntry.loadEventEnd - navEntry.loadEventStart} ms`
    });
  }
};
